/*
 * Copyright (c) 2022, Salesforce, Inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import {defineMessage, defineMessages} from 'react-intl'

// Global app defaults
export const DEFAULT_LOCALE = 'en-US'
export const DEFAULT_SITE_TITLE = defineMessage({
    id: 'global.page.default_site_title',
    defaultMessage: 'Children´s High Chair, Strollers & Nursery'
})
export const MAX_CACHE_AGE = 60 * 15 // 15 min
export const STALE_WHILE_REVALIDATE = 60 * 15 // 15 min

export const SITE_TYPES = {
    CONTENT_ONLY: 'ContentOnly',
    ECOM: 'Ecom',
    PARTNER_ECOM: 'PartnerEcom'
}

// Constants used in the used for product searching.
export const DEFAULT_CATEGORY_PARAMS = {limit: 38, offset: 0, sort: 'best-matches', refine: []}
export const DEFAULT_SEARCH_PARAMS = {limit: 12, offset: 0, sort: 'best-matches', refine: []}
export const DEFAULT_LIMIT_VALUES = [25, 50, 100] // Page sizes

// Constants for Search Component
export const RECENT_SEARCH_LIMIT = 5
export const RECENT_SEARCH_KEY = 'recent-search-key'
export const RECENT_SEARCH_MIN_LENGTH = 3

// PDP display template types
export const CUSTOMIZABLE_SET_DISPLAY_TEMPLATE = 'customizable-set'

// Constants for the Homepage's Shop Products section.
export const HOME_SHOP_PRODUCTS_CATEGORY_ID = 'newarrivals'
export const HOME_SHOP_PRODUCTS_LIMIT = 10

// Constant for the my account orders section.
export const MY_ACCOUNT_ORDERS_LIMIT = 50

// Constants for menu
export const CAT_MENU_DEFAULT_NAV_DEPTH = 1
export const CAT_MENU_DEFAULT_ROOT_CATEGORY = 'root'
export const CAT_MENU_STALE_TIME = 10000 // 10 seconds

// Constants used for differentiation of customer types.
export const CUSTOMER_TYPE_B2C = 'b2c'
export const CUSTOMER_TYPE_D2B = 'd2b'

// Color to use for the UI surrounding the page in browsers.
export const THEME_COLOR = '#ffffff'

export const FILTER_ACCORDION_SATE = 'filters-expanded-index'

export const LOGIN_INACTIVE_ERROR_MESSAGE = defineMessage({
    defaultMessage:
        'In order to login, you have to activate your account from the link we sent you in an email.',
    id: 'account-registration.msg.double_opt_in'
})

export const API_ERROR_MESSAGE = defineMessage({
    id: 'global.error.something_went_wrong',
    defaultMessage: 'Hmmm! Something went wrong. Please try again.'
})
export const INVALID_TOKEN_ERROR_MESSAGE = defineMessage({
    defaultMessage: 'Invalid token, please try again.',
    id: 'global.error.invalid_token'
})
export const FEATURE_UNAVAILABLE_ERROR_MESSAGE = defineMessage({
    defaultMessage: 'This feature is not currently available.',
    id: 'global.error.feature_unavailable'
})
export const CREATE_ACCOUNT_FIRST_ERROR_MESSAGE = defineMessage({
    defaultMessage:
        'This feature is not currently available. You must create an account to access this feature.',
    id: 'global.error.create_account'
})

// route constants
export const HOME_HREF = '/home'
export const CART_HREF = '/appcart'
export const CPSIA_HREF = '/cpsia'
export const SIGN_IN_HREF = '/login'
export const REGISTRATION_HREF = '/registration'
export const RESET_PASSWORD_HREF = '/reset-password'
export const STORE_LOCATOR_HREF = '/stores'
export const ACCOUNT_HREF = '/my-stokke'
export const CHECKOUT_HREF = '/appcheckout'
export const COUNTRY_SELECTOR_HREF = '/country-selector'
export const CONTACT_US_HREF = '/contact-us'
export const CONTACT_LANDING_HREF = '/customer-service/customer-service-contact-form.html'
export const FAQ_HREF = '/customer-service/faq/faq.html'
export const CUSTOMER_SERVICE_HREF = '/customer-service'
export const WARRANTY_REGISTER_HREF = '/warranty'
export const WISHLIST_HREF = '/my-stokke/wishlist'
export const ORDERSTATUS_HREF = '/orderstatus'
export const urlPartPositions = {
    PATH: 'path',
    QUERY_PARAM: 'query_param',
    NONE: 'none'
}
export const PDP_ROUTE_PATH = '/*/:productId.html'
export const PLP_ROUTE_PATH = '/:categoryPath'

export const MAX_ORDER_QUANTITY = 10

// Toast messages exist outside the scope of the base IntlProvider. This means
// that commonly used components that require localization cannot easily be
// extracted into a hook/helper. However, we can still extract the message
// definitions to a common location (here), so that each message is only defined
// once.

export const TOAST_MESSAGE_MERGE_WISHLIST = defineMessage({
    id: 'global.info.merge_wishlist',
    defaultMessage: 'Your wishlist has been transferred to your account wishlist on My Stokke'
})

export const TOAST_MESSAGE_ADDED_TO_WISHLIST = defineMessage({
    id: 'global.info.added_to_wishlist',
    defaultMessage: '{quantity} {quantity, plural, one {item} other {items}} added to wishlist'
})

export const TOAST_ACTION_VIEW_WISHLIST = defineMessage({
    defaultMessage: 'View',
    id: 'global.link.added_to_wishlist.view_wishlist'
})

export const TOAST_MESSAGE_REMOVED_FROM_WISHLIST = defineMessage({
    id: 'global.info.removed_from_wishlist',
    defaultMessage: 'Item removed from wishlist'
})

export const ACCOUNT_REGISTRATION_FORM_DATA = 'account_registration_data'

export const ADYEN_CONFIGURATION = 'adyen_configuration'

export const CHECKOUT_TOKEN = 'checkout_token'
export const PAYMENT_OPTION = 'payment_option'
export const CHECKOUT_ORDER_ID = 'checkout_order_id'

export const FAILED_BASKET_ID = 'failed_basket_id'

export const FAIL_ORDER_ERROR_MESSAGE = defineMessage({
    id: 'global.error.payment.failed',
    defaultMessage: 'Order was cancelled because payment has failed/refused'
})

// Delay before scrolling up to payments, will be removed if we get event to do the same from Adyen
export const PAYMENT_CHALLENGE_DELAY = 2000

// used in case of selection of back button that does not reload the previous page
export const PAGE_SHOW_LISTENER = function (event) {
    if (event.persisted) {
        window.location.reload()
    }
}

// used for cms scripts to be executed on page load
export const CMS_SCRIPTS_MIME_TYPE = 'application/x-pwa-cms-script'

// used to predefine MY-STOKKE headline and subline
export const MY_STOKKE_CONFIG_LABELS = {
    addresses: {
        headline: defineMessage({
            id: 'mystokke.addresses.headline',
            defaultMessage: 'Addresses'
        }),
        subline: defineMessage({
            id: 'mystokke.addresses.subline',
            defaultMessage: ''
        })
    },
    orders: {
        headline: defineMessage({
            id: 'mystokke.orders.headline',
            defaultMessage: 'Orders'
        }),
        subline: defineMessage({
            id: 'mystokke.orders.subline',
            defaultMessage: ''
        })
    },
    'personal-information': {
        headline: defineMessage({
            id: 'mystokke.personal-information.headline',
            defaultMessage: 'Personal information'
        }),
        subline: defineMessage({
            id: 'mystokke.personal-information.subline',
            defaultMessage: ''
        })
    },
    warranty: {
        headline: defineMessage({
            id: 'mystokke.warranty.headline',
            defaultMessage: 'Product Registration & Warranties'
        }),
        subline: defineMessage({
            id: 'mystokke.warranty.subline',
            defaultMessage: 'Register your Stokke products to activate or extend your warranty.'
        })
    },
    wishlist: {
        headline: defineMessage({
            id: 'mystokke.wishlist.headline',
            defaultMessage: 'Wishlist'
        }),
        subline: defineMessage({
            id: 'mystokke.wishlist.subline',
            defaultMessage: ''
        })
    }
}

export const MY_STOKKE_CONFIG_LABELS_D2B = {
    ...MY_STOKKE_CONFIG_LABELS,
    'personal-information': {
        headline: defineMessage({
            id: 'mystokke.company-information.headline',
            defaultMessage: 'Company information'
        }),
        subline: defineMessage({
            id: 'mystokke.company-information.subline',
            defaultMessage: 'Manage company details here.'
        })
    }
}

// used for address field salutations
export const SALUTATION_OPTIONS = [
    {
        value: 'Mr',
        label: defineMessage({
            id: 'use_address_fields.field.salutation.option1',
            defaultMessage: 'Mr'
        }),
        display: true
    },
    {
        value: 'Mrs',
        label: defineMessage({
            id: 'use_address_fields.field.salutation.option2',
            defaultMessage: 'Mrs'
        }),
        display: true
    },
    {
        value: 'Other',
        label: defineMessage({
            id: 'use_address_fields.field.salutation.option5',
            defaultMessage: 'Other'
        }),
        display: false
    }
]

// used to render additional information for each ADYEN payment option
export const ADYEN_TOОLTIPS = defineMessages({
    // as of now there are no tooltips defined for any payment method
    directEbanking: {
        id: 'adyen.tooltips.directEbanking',
        defaultMessage:
            'Immediate bank transfer via your Klarna account. Payment is confirmed in real time.'
    },
    // eps: {
    //     id: 'adyen.tooltips.eps',
    //     defaultMessage: 'Additional information about eps'
    // },
    // paypal: {
    //     id: 'adyen.tooltips.paypal',
    //     defaultMessage: 'Additional information about paypal'
    // },
    // applepay: {
    //     id: 'adyen.tooltips.applepay',
    //     defaultMessage: 'Additional information about applepay'
    // },
    klarna: {
        id: 'adyen.tooltips.klarna',
        defaultMessage:
            'Buy now and pay within 30 days, interest-free. Receive your order first, then decide.'
    },
    klarna_account: {
        id: 'adyen.tooltips.klarna_account',
        defaultMessage:
            'Split your total into flexible monthly installments. Choose your plan directly with Klarna.'
    },
    klarna_paynow: {
        id: 'adyen.tooltips.klarna_paynow',
        defaultMessage:
            'Pay instantly via online bank transfer, directly from your account. No card needed.'
    }
    // swish: {
    //     id: 'adyen.tooltips.swish',
    //     defaultMessage: 'Additional information about swish'
    // },
    // clearpay: {
    //     id: 'adyen.tooltips.clearpay',
    //     defaultMessage: 'Additional information about clearpay'
    // },
    // paypay: {
    //     id: 'adyen.tooltips.paypay',
    //     defaultMessage: 'Additional information about paypay'
    // },
    // unionpay: {
    //     id: 'adyen.tooltips.unionpay',
    //     defaultMessage: 'Additional information about unionpay'
    // },
    // paybybank: {
    //     id: 'adyen.tooltips.paybybank',
    //     defaultMessage: 'Additional information about paybybank'
    // },
    // afterpaytouch_US: {
    //     id: 'adyen.tooltips.afterpaytouch_US',
    //     defaultMessage: 'Additional information about afterpaytouch_US'
    // },
    // bcmc: {
    //     id: 'adyen.tooltips.bcmc',
    //     defaultMessage: 'Additional information about bcmc'
    // },
    // mobilepay: {
    //     id: 'adyen.tooltips.mobilepay',
    //     defaultMessage: 'Additional information about mobilepay'
    // },
    // trustly: {
    //     id: 'adyen.tooltips.trustly',
    //     defaultMessage: 'Additional information about trustly'
    // },
    // ideal: {
    //     id: 'adyen.tooltips.ideal',
    //     defaultMessage: 'Additional information about ideal'
    // },
    // vipps: {
    //     id: 'adyen.tooltips.vipps',
    //     defaultMessage: 'Additional information about vipps'
    // },
    // blik: {
    //     id: 'adyen.tooltips.blik',
    //     defaultMessage: 'Additional information about blik'
    // },
    // afterpaytouch: {
    //     id: 'adyen.tooltips.afterpaytouch',
    //     defaultMessage: 'Additional information about afterpaytouch'
    // },
    // kcp_banktransfer: {
    //     id: 'adyen.tooltips.kcp_banktransfer',
    //     defaultMessage: 'Additional information about kcp_banktransfer'
    // },
    // kcp_naverpay: {
    //     id: 'adyen.tooltips.kcp_naverpay',
    //     defaultMessage: 'Additional information about kcp_naverpay'
    // },
    // kakaopay: {
    //     id: 'adyen.tooltips.kakaopay',
    //     defaultMessage: 'Additional information about kakaopay'
    // },
    // kcp_payco: {
    //     id: 'adyen.tooltips.kcp_payco',
    //     defaultMessage: 'Additional information about kcp_payco'
    // },
    // kcp_creditcard: {
    //     id: 'adyen.tooltips.kcp_creditcard',
    //     defaultMessage: 'Additional information about kcp_creditcard'
    // },
    // scheme: {
    //     id: 'adyen.tooltips.scheme',
    //     defaultMessage: 'Additional information about cards'
    // }
})

// used for point of purchase options
export const POINTOFPURCHASE_OPTIONS = {
    store: {
        label: defineMessage({
            id: 'warranty.pop.in_store',
            defaultMessage: 'In a store'
        }),
        display: true
    },
    online: {
        label: defineMessage({
            id: 'warranty.pop.online',
            defaultMessage: 'Online'
        }),
        display: true
    },
    gift: {
        label: defineMessage({
            id: 'warranty.pop.gift',
            defaultMessage: 'Gift'
        }),
        display: true
    }
}

// Used by Locale Selector which needs the country/language pairs
export const LOCALE_NAMES = {
    'da-DK': 'Dansk',
    'de-AT': 'Deutsch',
    'de-CH': 'Deutsch',
    'de-DE': 'Deutsch',
    'de-LU': 'Deutsch',
    'en-AE': 'English',
    'en-AL': 'English',
    'en-AM': 'English',
    'en-AU': 'English',
    'en-AZ': 'English',
    'en-BA': 'English',
    'en-BG': 'English',
    'en-BY': 'English',
    'en-CA': 'English',
    'en-CY': 'English',
    'en-CZ': 'English',
    'en-EE': 'English',
    'en-EG': 'English',
    'en-FI': 'English',
    'en-GB': 'English',
    'en-GE': 'English',
    'en-GR': 'English',
    'en-HK': 'English',
    'en-HR': 'English',
    'en-HU': 'English',
    'en-IE': 'English',
    'en-IL': 'English',
    'en-IN': 'English',
    'en-IR': 'English',
    'en-IS': 'English',
    'en-LT': 'English',
    'en-LV': 'English',
    'en-MY': 'English',
    'en-NZ': 'English',
    'en-PT': 'English',
    'en-QA': 'English',
    'en-RO': 'English',
    'en-RS': 'English',
    'en-SG': 'English',
    'en-SI': 'English',
    'en-SK': 'English',
    'en-US': 'English',
    'en-ZA': 'English',
    'es-AR': 'Español',
    'es-CL': 'Español',
    'es-CO': 'Español',
    'es-CR': 'Español',
    'es-ES': 'Español',
    'es-MX': 'Español',
    'es-PA': 'Español',
    'es-PE': 'Español',
    'es-PR': 'Español',
    'fr-BE': 'Français',
    'fr-CH': 'Français',
    'fr-FR': 'Français',
    'fr-LU': 'Français',
    'fr-CA': 'Français',
    'it-CH': 'Italiano',
    'it-IT': 'Italiano',
    'ja-JP': '日本語',
    'ko-KR': '한국어',
    'nl-BE': 'Nederlands',
    'nl-NL': 'Nederlands',
    'no-NO': 'Norsk',
    'pl-PL': 'Polski',
    'pt-BR': 'Português',
    'en-UA': 'Ukraine',
    'sv-SE': 'Svenska',
    'tr-TR': 'Türk',
    'zh-CN': '简体中文',
    'zh-HK': '香港粵語',
    'zh-TW': '國語'
}

export const COUNTRY_MESSAGES = defineMessages({
    AC: {id: 'country.ac', defaultMessage: 'Ascension Island'},
    AD: {id: 'country.ad', defaultMessage: 'Andorra'},
    AE: {id: 'country.ae', defaultMessage: 'United Arab Emirates'},
    AF: {id: 'country.af', defaultMessage: 'Afghanistan'},
    AG: {id: 'country.ag', defaultMessage: 'Antigua and Barbuda'},
    AI: {id: 'country.ai', defaultMessage: 'Anguilla'},
    AL: {id: 'country.al', defaultMessage: 'Albania'},
    AM: {id: 'country.am', defaultMessage: 'Armenia'},
    AO: {id: 'country.ao', defaultMessage: 'Angola'},
    AQ: {id: 'country.aq', defaultMessage: 'Antarctica'},
    AR: {id: 'country.ar', defaultMessage: 'Argentina'},
    AS: {id: 'country.as', defaultMessage: 'American Samoa'},
    AT: {id: 'country.at', defaultMessage: 'Austria'},
    AU: {id: 'country.au', defaultMessage: 'Australia'},
    AW: {id: 'country.aw', defaultMessage: 'Aruba'},
    AX: {id: 'country.ax', defaultMessage: 'Åland Islands'},
    AZ: {id: 'country.az', defaultMessage: 'Azerbaijan'},
    BA: {id: 'country.ba', defaultMessage: 'Bosnia and Herzegovina'},
    BB: {id: 'country.bb', defaultMessage: 'Barbados'},
    BD: {id: 'country.bd', defaultMessage: 'Bangladesh'},
    BE: {id: 'country.be', defaultMessage: 'Belgium'},
    BF: {id: 'country.bf', defaultMessage: 'Burkina Faso'},
    BG: {id: 'country.bg', defaultMessage: 'Bulgaria'},
    BH: {id: 'country.bh', defaultMessage: 'Bahrain'},
    BI: {id: 'country.bi', defaultMessage: 'Burundi'},
    BJ: {id: 'country.bj', defaultMessage: 'Benin'},
    BL: {id: 'country.bl', defaultMessage: 'Saint Barthélemy'},
    BM: {id: 'country.bm', defaultMessage: 'Bermuda'},
    BN: {id: 'country.bn', defaultMessage: 'Brunei'},
    BO: {id: 'country.bo', defaultMessage: 'Bolivia'},
    BQ: {id: 'country.bq', defaultMessage: 'Caribbean Netherlands'},
    BR: {id: 'country.br', defaultMessage: 'Brazil'},
    BS: {id: 'country.bs', defaultMessage: 'Bahamas'},
    BT: {id: 'country.bt', defaultMessage: 'Bhutan'},
    BW: {id: 'country.bw', defaultMessage: 'Botswana'},
    BY: {id: 'country.by', defaultMessage: 'Belarus'},
    BZ: {id: 'country.bz', defaultMessage: 'Belize'},
    CA: {id: 'country.ca', defaultMessage: 'Canada'},
    CC: {id: 'country.cc', defaultMessage: 'Cocos (Keeling) Islands'},
    CD: {id: 'country.cd', defaultMessage: 'DR Congo'},
    CF: {id: 'country.cf', defaultMessage: 'Central African Republic'},
    CG: {id: 'country.cg', defaultMessage: 'Congo'},
    CH: {id: 'country.ch', defaultMessage: 'Switzerland'},
    CI: {id: 'country.ci', defaultMessage: 'Ivory Coast'},
    CK: {id: 'country.ck', defaultMessage: 'Cook Islands'},
    CL: {id: 'country.cl', defaultMessage: 'Chile'},
    CM: {id: 'country.cm', defaultMessage: 'Cameroon'},
    CN: {id: 'country.cn', defaultMessage: 'China'},
    CO: {id: 'country.co', defaultMessage: 'Colombia'},
    CR: {id: 'country.cr', defaultMessage: 'Costa Rica'},
    CU: {id: 'country.cu', defaultMessage: 'Cuba'},
    CV: {id: 'country.cv', defaultMessage: 'Cape Verde'},
    CW: {id: 'country.cw', defaultMessage: 'Curaçao'},
    CX: {id: 'country.cx', defaultMessage: 'Christmas Island'},
    CY: {id: 'country.cy', defaultMessage: 'Cyprus'},
    CZ: {id: 'country.cz', defaultMessage: 'Czechia'},
    DE: {id: 'country.de', defaultMessage: 'Germany'},
    DG: {id: 'country.dg', defaultMessage: 'Diego Garcia'},
    DJ: {id: 'country.dj', defaultMessage: 'Djibouti'},
    DK: {id: 'country.dk', defaultMessage: 'Denmark'},
    DM: {id: 'country.dm', defaultMessage: 'Dominica'},
    DO: {id: 'country.do', defaultMessage: 'Dominican Republic'},
    DZ: {id: 'country.dz', defaultMessage: 'Algeria'},
    EC: {id: 'country.ec', defaultMessage: 'Ecuador'},
    EE: {id: 'country.ee', defaultMessage: 'Estonia'},
    EG: {id: 'country.eg', defaultMessage: 'Egypt'},
    ER: {id: 'country.er', defaultMessage: 'Eritrea'},
    ES: {id: 'country.es', defaultMessage: 'Spain'},
    ET: {id: 'country.et', defaultMessage: 'Ethiopia'},
    EU: {id: 'country.eu', defaultMessage: 'Other EU'},
    FI: {id: 'country.fi', defaultMessage: 'Finland'},
    FJ: {id: 'country.fj', defaultMessage: 'Fiji'},
    FK: {id: 'country.fk', defaultMessage: 'Falkland Islands'},
    FM: {id: 'country.fm', defaultMessage: 'Micronesia'},
    FO: {id: 'country.fo', defaultMessage: 'Faroe Islands'},
    FR: {id: 'country.fr', defaultMessage: 'France'},
    GA: {id: 'country.ga', defaultMessage: 'Gabon'},
    GB: {id: 'country.gb', defaultMessage: 'United Kingdom'},
    GD: {id: 'country.gd', defaultMessage: 'Grenada'},
    GE: {id: 'country.ge', defaultMessage: 'Georgia'},
    GF: {id: 'country.gf', defaultMessage: 'French Guiana'},
    GG: {id: 'country.gg', defaultMessage: 'Guernsey'},
    GH: {id: 'country.gh', defaultMessage: 'Ghana'},
    GI: {id: 'country.gi', defaultMessage: 'Gibraltar'},
    GL: {id: 'country.gl', defaultMessage: 'Greenland'},
    GM: {id: 'country.gm', defaultMessage: 'Gambia'},
    GN: {id: 'country.gn', defaultMessage: 'Guinea'},
    GP: {id: 'country.gp', defaultMessage: 'Guadeloupe'},
    GQ: {id: 'country.gq', defaultMessage: 'Equatorial Guinea'},
    GR: {id: 'country.gr', defaultMessage: 'Greece'},
    GT: {id: 'country.gt', defaultMessage: 'Guatemala'},
    GU: {id: 'country.gu', defaultMessage: 'Guam'},
    GW: {id: 'country.gw', defaultMessage: 'Guinea-Bissau'},
    GY: {id: 'country.gy', defaultMessage: 'Guyana'},
    HK: {id: 'country.hk', defaultMessage: 'Hong Kong'},
    HN: {id: 'country.hn', defaultMessage: 'Honduras'},
    HR: {id: 'country.hr', defaultMessage: 'Croatia'},
    HT: {id: 'country.ht', defaultMessage: 'Haiti'},
    HU: {id: 'country.hu', defaultMessage: 'Hungary'},
    ID: {id: 'country.id', defaultMessage: 'Indonesia'},
    IE: {id: 'country.ie', defaultMessage: 'Ireland'},
    IL: {id: 'country.il', defaultMessage: 'Israel'},
    IM: {id: 'country.im', defaultMessage: 'Isle of Man'},
    IN: {id: 'country.in', defaultMessage: 'India'},
    IO: {id: 'country.io', defaultMessage: 'British Indian Ocean Territory'},
    IQ: {id: 'country.iq', defaultMessage: 'Iraq'},
    IR: {id: 'country.ir', defaultMessage: 'Iran'},
    IS: {id: 'country.is', defaultMessage: 'Iceland'},
    IT: {id: 'country.it', defaultMessage: 'Italy'},
    JE: {id: 'country.je', defaultMessage: 'Jersey'},
    JM: {id: 'country.jm', defaultMessage: 'Jamaica'},
    JO: {id: 'country.jo', defaultMessage: 'Jordan'},
    JP: {id: 'country.jp', defaultMessage: 'Japan'},
    KE: {id: 'country.ke', defaultMessage: 'Kenya'},
    KG: {id: 'country.kg', defaultMessage: 'Kyrgyzstan'},
    KH: {id: 'country.kh', defaultMessage: 'Cambodia'},
    KI: {id: 'country.ki', defaultMessage: 'Kiribati'},
    KM: {id: 'country.km', defaultMessage: 'Comoros'},
    KN: {id: 'country.kn', defaultMessage: 'Saint Kitts and Nevis'},
    KP: {id: 'country.kp', defaultMessage: 'North Korea'},
    KR: {id: 'country.kr', defaultMessage: 'South Korea'},
    KW: {id: 'country.kw', defaultMessage: 'Kuwait'},
    KY: {id: 'country.ky', defaultMessage: 'Cayman Islands'},
    KZ: {id: 'country.kz', defaultMessage: 'Kazakhstan'},
    LA: {id: 'country.la', defaultMessage: 'Laos'},
    LB: {id: 'country.lb', defaultMessage: 'Lebanon'},
    LC: {id: 'country.lc', defaultMessage: 'Saint Lucia'},
    LI: {id: 'country.li', defaultMessage: 'Liechtenstein'},
    LK: {id: 'country.lk', defaultMessage: 'Sri Lanka'},
    LR: {id: 'country.lr', defaultMessage: 'Liberia'},
    LS: {id: 'country.ls', defaultMessage: 'Lesotho'},
    LT: {id: 'country.lt', defaultMessage: 'Lithuania'},
    LU: {id: 'country.lu', defaultMessage: 'Luxembourg'},
    LV: {id: 'country.lv', defaultMessage: 'Latvia'},
    LY: {id: 'country.ly', defaultMessage: 'Libya'},
    MA: {id: 'country.ma', defaultMessage: 'Morocco'},
    MC: {id: 'country.mc', defaultMessage: 'Monaco'},
    MD: {id: 'country.md', defaultMessage: 'Moldova'},
    ME: {id: 'country.me', defaultMessage: 'Montenegro'},
    MF: {id: 'country.mf', defaultMessage: 'Saint Martin'},
    MG: {id: 'country.mg', defaultMessage: 'Madagascar'},
    MH: {id: 'country.mh', defaultMessage: 'Marshall Islands'},
    MK: {id: 'country.mk', defaultMessage: 'North Macedonia'},
    ML: {id: 'country.ml', defaultMessage: 'Mali'},
    MM: {id: 'country.mm', defaultMessage: 'Myanmar'},
    MN: {id: 'country.mn', defaultMessage: 'Mongolia'},
    MO: {id: 'country.mo', defaultMessage: 'Macau'},
    MP: {id: 'country.mp', defaultMessage: 'Northern Mariana Islands'},
    MQ: {id: 'country.mq', defaultMessage: 'Martinique'},
    MR: {id: 'country.mr', defaultMessage: 'Mauritania'},
    MS: {id: 'country.ms', defaultMessage: 'Montserrat'},
    MT: {id: 'country.mt', defaultMessage: 'Malta'},
    MU: {id: 'country.mu', defaultMessage: 'Mauritius'},
    MV: {id: 'country.mv', defaultMessage: 'Maldives'},
    MW: {id: 'country.mw', defaultMessage: 'Malawi'},
    MX: {id: 'country.mx', defaultMessage: 'Mexico'},
    MY: {id: 'country.my', defaultMessage: 'Malaysia'},
    MZ: {id: 'country.mz', defaultMessage: 'Mozambique'},
    NA: {id: 'country.na', defaultMessage: 'Namibia'},
    NC: {id: 'country.nc', defaultMessage: 'New Caledonia'},
    NE: {id: 'country.ne', defaultMessage: 'Niger'},
    NG: {id: 'country.ng', defaultMessage: 'Nigeria'},
    NI: {id: 'country.ni', defaultMessage: 'Nicaragua'},
    NL: {id: 'country.nl', defaultMessage: 'Netherlands'},
    NO: {id: 'country.no', defaultMessage: 'Norway'},
    NP: {id: 'country.np', defaultMessage: 'Nepal'},
    NR: {id: 'country.nr', defaultMessage: 'Nauru'},
    NU: {id: 'country.nu', defaultMessage: 'Niue'},
    NZ: {id: 'country.nz', defaultMessage: 'New Zealand'},
    OM: {id: 'country.om', defaultMessage: 'Oman'},
    PA: {id: 'country.pa', defaultMessage: 'Panama'},
    PE: {id: 'country.pe', defaultMessage: 'Peru'},
    PF: {id: 'country.pf', defaultMessage: 'French Polynesia'},
    PG: {id: 'country.pg', defaultMessage: 'Papua New Guinea'},
    PH: {id: 'country.ph', defaultMessage: 'Philippines'},
    PK: {id: 'country.pk', defaultMessage: 'Pakistan'},
    PL: {id: 'country.pl', defaultMessage: 'Poland'},
    PN: {id: 'country.pn', defaultMessage: 'Pitcairn Islands'},
    PM: {id: 'country.pm', defaultMessage: 'Saint Pierre and Miquelon'},
    PR: {id: 'country.pr', defaultMessage: 'Puerto Rico'},
    PS: {id: 'country.ps', defaultMessage: 'Palestine'},
    PT: {id: 'country.pt', defaultMessage: 'Portugal'},
    PW: {id: 'country.pw', defaultMessage: 'Palau'},
    PY: {id: 'country.py', defaultMessage: 'Paraguay'},
    QA: {id: 'country.qa', defaultMessage: 'Qatar'},
    RE: {id: 'country.re', defaultMessage: 'Réunion'},
    RO: {id: 'country.ro', defaultMessage: 'Romania'},
    RS: {id: 'country.rs', defaultMessage: 'Serbia'},
    RU: {id: 'country.ru', defaultMessage: 'Russia'},
    RW: {id: 'country.rw', defaultMessage: 'Rwanda'},
    SA: {id: 'country.sa', defaultMessage: 'Saudi Arabia'},
    SB: {id: 'country.sb', defaultMessage: 'Solomon Islands'},
    SC: {id: 'country.sc', defaultMessage: 'Seychelles'},
    SD: {id: 'country.sd', defaultMessage: 'Sudan'},
    SE: {id: 'country.se', defaultMessage: 'Sweden'},
    SG: {id: 'country.sg', defaultMessage: 'Singapore'},
    SH: {id: 'country.sh', defaultMessage: 'Saint Helena, Ascension and Tristan da Cunha'},
    SI: {id: 'country.si', defaultMessage: 'Slovenia'},
    SK: {id: 'country.sk', defaultMessage: 'Slovakia'},
    SL: {id: 'country.sl', defaultMessage: 'Sierra Leone'},
    SM: {id: 'country.sm', defaultMessage: 'San Marino'},
    SN: {id: 'country.sn', defaultMessage: 'Senegal'},
    SO: {id: 'country.so', defaultMessage: 'Somalia'},
    SR: {id: 'country.sr', defaultMessage: 'Suriname'},
    SS: {id: 'country.ss', defaultMessage: 'South Sudan'},
    ST: {id: 'country.st', defaultMessage: 'São Tomé and Príncipe'},
    SV: {id: 'country.sv', defaultMessage: 'El Salvador'},
    SX: {id: 'country.sx', defaultMessage: 'Sint Maarten'},
    SY: {id: 'country.sy', defaultMessage: 'Syria'},
    SZ: {id: 'country.sz', defaultMessage: 'Eswatini'},
    TC: {id: 'country.tc', defaultMessage: 'Turks and Caicos Islands'},
    TD: {id: 'country.td', defaultMessage: 'Chad'},
    TG: {id: 'country.tg', defaultMessage: 'Togo'},
    TH: {id: 'country.th', defaultMessage: 'Thailand'},
    TJ: {id: 'country.tj', defaultMessage: 'Tajikistan'},
    TK: {id: 'country.tk', defaultMessage: 'Tokelau'},
    TL: {id: 'country.tl', defaultMessage: 'Timor-Leste'},
    TM: {id: 'country.tm', defaultMessage: 'Turkmenistan'},
    TN: {id: 'country.tn', defaultMessage: 'Tunisia'},
    TO: {id: 'country.to', defaultMessage: 'Tonga'},
    TR: {id: 'country.tr', defaultMessage: 'Türkiye'},
    TT: {id: 'country.tt', defaultMessage: 'Trinidad and Tobago'},
    TV: {id: 'country.tv', defaultMessage: 'Tuvalu'},
    TW: {id: 'country.tw', defaultMessage: 'Taiwan - Greater China'},
    TZ: {id: 'country.tz', defaultMessage: 'Tanzania'},
    UA: {id: 'country.ua', defaultMessage: 'Ukraine'},
    UG: {id: 'country.ug', defaultMessage: 'Uganda'},
    UK: {id: 'country.uk', defaultMessage: 'United Kingdom'},
    US: {id: 'country.us', defaultMessage: 'United States'},
    UY: {id: 'country.uy', defaultMessage: 'Uruguay'},
    UZ: {id: 'country.uz', defaultMessage: 'Uzbekistan'},
    VA: {id: 'country.va', defaultMessage: 'Vatican City'},
    VC: {id: 'country.vc', defaultMessage: 'Saint Vincent and the Grenadines'},
    VE: {id: 'country.ve', defaultMessage: 'Venezuela'},
    VG: {id: 'country.vg', defaultMessage: 'British Virgin Islands'},
    VI: {id: 'country.vi', defaultMessage: 'United States Virgin Islands'},
    VN: {id: 'country.vn', defaultMessage: 'Vietnam'},
    VU: {id: 'country.vu', defaultMessage: 'Vanuatu'},
    WF: {id: 'country.wf', defaultMessage: 'Wallis and Futuna'},
    WS: {id: 'country.ws', defaultMessage: 'Samoa'},
    XB: {id: 'country.xb', defaultMessage: 'Wonderland'},
    XK: {id: 'country.xk', defaultMessage: 'Kosovo'},
    YE: {id: 'country.ye', defaultMessage: 'Yemen'},
    YT: {id: 'country.yt', defaultMessage: 'Mayotte'},
    ZA: {id: 'country.za', defaultMessage: 'South Africa'},
    ZM: {id: 'country.zm', defaultMessage: 'Zambia'},
    ZW: {id: 'country.zw', defaultMessage: 'Zimbabwe'}
})

export const PHONE_COUNTRIES = [
    {countryCode: 'AF', dialCode: '+93', placeholder: '123456789'},
    {countryCode: 'AX', dialCode: '+358', placeholder: '123456789'},
    {countryCode: 'AL', dialCode: '+355', placeholder: '123456789'},
    {countryCode: 'DZ', dialCode: '+213', placeholder: '123456789'},
    {countryCode: 'AS', dialCode: '+1', placeholder: '123456789'},
    {countryCode: 'AD', dialCode: '+376', placeholder: '123456789'},
    {countryCode: 'AO', dialCode: '+244', placeholder: '123456789'},
    {countryCode: 'AI', dialCode: '+1', placeholder: '123456789'},
    {countryCode: 'AQ', dialCode: '+672', placeholder: '123456789'},
    {countryCode: 'AG', dialCode: '+1', placeholder: '123456789'},
    {countryCode: 'AR', dialCode: '+54', placeholder: '123456789'},
    {countryCode: 'AM', dialCode: '+374', placeholder: '123456789'},
    {countryCode: 'AW', dialCode: '+297', placeholder: '123456789'},
    {countryCode: 'AC', dialCode: '+247', placeholder: '123456789'},
    {countryCode: 'AU', dialCode: '+61', placeholder: '412345678'},
    {countryCode: 'AT', dialCode: '+43', placeholder: '6641234567'},
    {countryCode: 'AZ', dialCode: '+994', placeholder: '123456789'},
    {countryCode: 'BS', dialCode: '+1', placeholder: '123456789'},
    {countryCode: 'BH', dialCode: '+973', placeholder: '123456789'},
    {countryCode: 'BD', dialCode: '+880', placeholder: '123456789'},
    {countryCode: 'BB', dialCode: '+1', placeholder: '123456789'},
    {countryCode: 'BY', dialCode: '+375', placeholder: '123456789'},
    {countryCode: 'BE', dialCode: '+32', placeholder: '470123456'},
    {countryCode: 'BZ', dialCode: '+501', placeholder: '123456789'},
    {countryCode: 'BJ', dialCode: '+229', placeholder: '123456789'},
    {countryCode: 'BM', dialCode: '+1', placeholder: '123456789'},
    {countryCode: 'BT', dialCode: '+975', placeholder: '123456789'},
    {countryCode: 'BO', dialCode: '+591', placeholder: '123456789'},
    {countryCode: 'BA', dialCode: '+387', placeholder: '123456789'},
    {countryCode: 'BW', dialCode: '+267', placeholder: '123456789'},
    {countryCode: 'BR', dialCode: '+55', placeholder: '123456789'},
    {countryCode: 'IO', dialCode: '+246', placeholder: '123456789'},
    {countryCode: 'VG', dialCode: '+1', placeholder: '123456789'},
    {countryCode: 'BN', dialCode: '+673', placeholder: '123456789'},
    {countryCode: 'BG', dialCode: '+359', placeholder: '881234567'},
    {countryCode: 'BF', dialCode: '+226', placeholder: '123456789'},
    {countryCode: 'BI', dialCode: '+257', placeholder: '123456789'},
    {countryCode: 'KH', dialCode: '+855', placeholder: '123456789'},
    {countryCode: 'CM', dialCode: '+237', placeholder: '123456789'},
    {countryCode: 'CA', dialCode: '+1', placeholder: '4165551234'},
    {countryCode: 'CV', dialCode: '+238', placeholder: '123456789'},
    {countryCode: 'BQ', dialCode: '+599', placeholder: '123456789'},
    {countryCode: 'KY', dialCode: '+1', placeholder: '123456789'},
    {countryCode: 'CF', dialCode: '+236', placeholder: '123456789'},
    {countryCode: 'TD', dialCode: '+235', placeholder: '123456789'},
    {countryCode: 'CL', dialCode: '+56', placeholder: '123456789'},
    {countryCode: 'CN', dialCode: '+86', placeholder: '123456789'},
    {countryCode: 'CX', dialCode: '+61', placeholder: '123456789'},
    {countryCode: 'CC', dialCode: '+61', placeholder: '123456789'},
    {countryCode: 'CO', dialCode: '+57', placeholder: '123456789'},
    {countryCode: 'KM', dialCode: '+269', placeholder: '123456789'},
    {countryCode: 'CG', dialCode: '+242', placeholder: '123456789'},
    {countryCode: 'CK', dialCode: '+682', placeholder: '123456789'},
    {countryCode: 'CR', dialCode: '+506', placeholder: '123456789'},
    {countryCode: 'HR', dialCode: '+385', placeholder: '911234567'},
    {countryCode: 'CU', dialCode: '+53', placeholder: '123456789'},
    {countryCode: 'CW', dialCode: '+599', placeholder: '123456789'},
    {countryCode: 'CY', dialCode: '+357', placeholder: '123456789'},
    {countryCode: 'CZ', dialCode: '+420', placeholder: '601123456'},
    {countryCode: 'DK', dialCode: '+45', placeholder: '20123456'},
    {countryCode: 'DG', dialCode: '+246', placeholder: '123456789'},
    {countryCode: 'DJ', dialCode: '+253', placeholder: '123456789'},
    {countryCode: 'DM', dialCode: '+1', placeholder: '123456789'},
    {countryCode: 'DO', dialCode: '+1', placeholder: '123456789'},
    {countryCode: 'CD', dialCode: '+243', placeholder: '123456789'},
    {countryCode: 'EC', dialCode: '+593', placeholder: '123456789'},
    {countryCode: 'EG', dialCode: '+20', placeholder: '123456789'},
    {countryCode: 'SV', dialCode: '+503', placeholder: '123456789'},
    {countryCode: 'GQ', dialCode: '+240', placeholder: '123456789'},
    {countryCode: 'ER', dialCode: '+291', placeholder: '123456789'},
    {countryCode: 'EE', dialCode: '+372', placeholder: '51234567'},
    {countryCode: 'SZ', dialCode: '+268', placeholder: '123456789'},
    {countryCode: 'ET', dialCode: '+251', placeholder: '123456789'},
    {countryCode: 'FK', dialCode: '+500', placeholder: '123456789'},
    {countryCode: 'FO', dialCode: '+298', placeholder: '123456789'},
    {countryCode: 'FJ', dialCode: '+679', placeholder: '123456789'},
    {countryCode: 'FI', dialCode: '+358', placeholder: '401234567'},
    {countryCode: 'FR', dialCode: '+33', placeholder: '612345678'},
    {countryCode: 'GF', dialCode: '+594', placeholder: '123456789'},
    {countryCode: 'PF', dialCode: '+689', placeholder: '123456789'},
    {countryCode: 'GA', dialCode: '+241', placeholder: '123456789'},
    {countryCode: 'GM', dialCode: '+220', placeholder: '123456789'},
    {countryCode: 'GE', dialCode: '+995', placeholder: '123456789'},
    {countryCode: 'DE', dialCode: '+49', placeholder: '1711234567'},
    {countryCode: 'GH', dialCode: '+233', placeholder: '123456789'},
    {countryCode: 'GI', dialCode: '+350', placeholder: '123456789'},
    {countryCode: 'GR', dialCode: '+30', placeholder: '123456789'},
    {countryCode: 'GL', dialCode: '+299', placeholder: '123456789'},
    {countryCode: 'GD', dialCode: '+1', placeholder: '123456789'},
    {countryCode: 'GP', dialCode: '+590', placeholder: '123456789'},
    {countryCode: 'GU', dialCode: '+1', placeholder: '123456789'},
    {countryCode: 'GT', dialCode: '+502', placeholder: '123456789'},
    {countryCode: 'GG', dialCode: '+44', placeholder: '123456789'},
    {countryCode: 'GN', dialCode: '+224', placeholder: '123456789'},
    {countryCode: 'GW', dialCode: '+245', placeholder: '123456789'},
    {countryCode: 'GY', dialCode: '+592', placeholder: '123456789'},
    {countryCode: 'HT', dialCode: '+509', placeholder: '123456789'},
    {countryCode: 'HN', dialCode: '+504', placeholder: '123456789'},
    {countryCode: 'HK', dialCode: '+852', placeholder: '123456789'},
    {countryCode: 'HU', dialCode: '+36', placeholder: '123456789'},
    {countryCode: 'IS', dialCode: '+354', placeholder: '123456789'},
    {countryCode: 'IN', dialCode: '+91', placeholder: '123456789'},
    {countryCode: 'ID', dialCode: '+62', placeholder: '123456789'},
    {countryCode: 'IR', dialCode: '+98', placeholder: '123456789'},
    {countryCode: 'IQ', dialCode: '+964', placeholder: '123456789'},
    {countryCode: 'IE', dialCode: '+353', placeholder: '851234567'},
    {countryCode: 'IM', dialCode: '+44', placeholder: '123456789'},
    {countryCode: 'IL', dialCode: '+972', placeholder: '123456789'},
    {countryCode: 'IT', dialCode: '+39', placeholder: '3331234567'},
    {countryCode: 'CI', dialCode: '+225', placeholder: '123456789'},
    {countryCode: 'JM', dialCode: '+1', placeholder: '123456789'},
    {countryCode: 'JP', dialCode: '+81', placeholder: '9012345678'},
    {countryCode: 'JE', dialCode: '+44', placeholder: '123456789'},
    {countryCode: 'JO', dialCode: '+962', placeholder: '123456789'},
    {countryCode: 'KZ', dialCode: '+7', placeholder: '123456789'},
    {countryCode: 'KE', dialCode: '+254', placeholder: '123456789'},
    {countryCode: 'KI', dialCode: '+686', placeholder: '123456789'},
    {countryCode: 'XK', dialCode: '+383', placeholder: '123456789'},
    {countryCode: 'KW', dialCode: '+965', placeholder: '123456789'},
    {countryCode: 'KG', dialCode: '+996', placeholder: '123456789'},
    {countryCode: 'LA', dialCode: '+856', placeholder: '123456789'},
    {countryCode: 'LV', dialCode: '+371', placeholder: '21234567'},
    {countryCode: 'LB', dialCode: '+961', placeholder: '123456789'},
    {countryCode: 'LS', dialCode: '+266', placeholder: '123456789'},
    {countryCode: 'LR', dialCode: '+231', placeholder: '123456789'},
    {countryCode: 'LY', dialCode: '+218', placeholder: '123456789'},
    {countryCode: 'LI', dialCode: '+423', placeholder: '123456789'},
    {countryCode: 'LT', dialCode: '+370', placeholder: '61234567'},
    {countryCode: 'LU', dialCode: '+352', placeholder: '621123456'},
    {countryCode: 'MO', dialCode: '+853', placeholder: '123456789'},
    {countryCode: 'MG', dialCode: '+261', placeholder: '123456789'},
    {countryCode: 'MW', dialCode: '+265', placeholder: '123456789'},
    {countryCode: 'MY', dialCode: '+60', placeholder: '123456789'},
    {countryCode: 'MV', dialCode: '+960', placeholder: '123456789'},
    {countryCode: 'ML', dialCode: '+223', placeholder: '123456789'},
    {countryCode: 'MT', dialCode: '+356', placeholder: '123456789'},
    {countryCode: 'MH', dialCode: '+692', placeholder: '123456789'},
    {countryCode: 'MQ', dialCode: '+596', placeholder: '123456789'},
    {countryCode: 'MR', dialCode: '+222', placeholder: '123456789'},
    {countryCode: 'MU', dialCode: '+230', placeholder: '123456789'},
    {countryCode: 'YT', dialCode: '+262', placeholder: '123456789'},
    {countryCode: 'MX', dialCode: '+52', placeholder: '123456789'},
    {countryCode: 'FM', dialCode: '+691', placeholder: '123456789'},
    {countryCode: 'MD', dialCode: '+373', placeholder: '123456789'},
    {countryCode: 'MC', dialCode: '+377', placeholder: '123456789'},
    {countryCode: 'MN', dialCode: '+976', placeholder: '123456789'},
    {countryCode: 'ME', dialCode: '+382', placeholder: '123456789'},
    {countryCode: 'MS', dialCode: '+1', placeholder: '123456789'},
    {countryCode: 'MA', dialCode: '+212', placeholder: '123456789'},
    {countryCode: 'MZ', dialCode: '+258', placeholder: '123456789'},
    {countryCode: 'MM', dialCode: '+95', placeholder: '123456789'},
    {countryCode: 'NA', dialCode: '+264', placeholder: '123456789'},
    {countryCode: 'NR', dialCode: '+674', placeholder: '123456789'},
    {countryCode: 'NP', dialCode: '+977', placeholder: '123456789'},
    {countryCode: 'NL', dialCode: '+31', placeholder: '612345678'},
    {countryCode: 'NC', dialCode: '+687', placeholder: '123456789'},
    {countryCode: 'NZ', dialCode: '+64', placeholder: '123456789'},
    {countryCode: 'NI', dialCode: '+505', placeholder: '123456789'},
    {countryCode: 'NE', dialCode: '+227', placeholder: '123456789'},
    {countryCode: 'NG', dialCode: '+234', placeholder: '123456789'},
    {countryCode: 'NU', dialCode: '+683', placeholder: '123456789'},
    {countryCode: 'KP', dialCode: '+850', placeholder: '123456789'},
    {countryCode: 'MK', dialCode: '+389', placeholder: '123456789'},
    {countryCode: 'MP', dialCode: '+1', placeholder: '123456789'},
    {countryCode: 'NO', dialCode: '+47', placeholder: '123456789'},
    {countryCode: 'OM', dialCode: '+968', placeholder: '123456789'},
    {countryCode: 'PK', dialCode: '+92', placeholder: '123456789'},
    {countryCode: 'PW', dialCode: '+680', placeholder: '123456789'},
    {countryCode: 'PS', dialCode: '+970', placeholder: '123456789'},
    {countryCode: 'PA', dialCode: '+507', placeholder: '123456789'},
    {countryCode: 'PG', dialCode: '+675', placeholder: '123456789'},
    {countryCode: 'PY', dialCode: '+595', placeholder: '123456789'},
    {countryCode: 'PE', dialCode: '+51', placeholder: '123456789'},
    {countryCode: 'PH', dialCode: '+63', placeholder: '123456789'},
    {countryCode: 'PN', dialCode: '+64', placeholder: '123456789'},
    {countryCode: 'PL', dialCode: '+48', placeholder: '500123456'},
    {countryCode: 'PT', dialCode: '+351', placeholder: '912345678'},
    {countryCode: 'PR', dialCode: '+1', placeholder: '123456789'},
    {countryCode: 'QA', dialCode: '+974', placeholder: '123456789'},
    {countryCode: 'RE', dialCode: '+262', placeholder: '123456789'},
    {countryCode: 'RO', dialCode: '+40', placeholder: '722123456'},
    {countryCode: 'RU', dialCode: '+7', placeholder: '123456789'},
    {countryCode: 'RW', dialCode: '+250', placeholder: '123456789'},
    {countryCode: 'BL', dialCode: '+590', placeholder: '123456789'},
    {countryCode: 'SH', dialCode: '+290', placeholder: '123456789'},
    {countryCode: 'KN', dialCode: '+1', placeholder: '123456789'},
    {countryCode: 'LC', dialCode: '+1', placeholder: '123456789'},
    {countryCode: 'MF', dialCode: '+590', placeholder: '123456789'},
    {countryCode: 'PM', dialCode: '+508', placeholder: '123456789'},
    {countryCode: 'VC', dialCode: '+1', placeholder: '123456789'},
    {countryCode: 'WS', dialCode: '+685', placeholder: '123456789'},
    {countryCode: 'SM', dialCode: '+378', placeholder: '123456789'},
    {countryCode: 'ST', dialCode: '+239', placeholder: '123456789'},
    {countryCode: 'SA', dialCode: '+966', placeholder: '123456789'},
    {countryCode: 'SN', dialCode: '+221', placeholder: '123456789'},
    {countryCode: 'RS', dialCode: '+381', placeholder: '123456789'},
    {countryCode: 'SC', dialCode: '+248', placeholder: '123456789'},
    {countryCode: 'SL', dialCode: '+232', placeholder: '123456789'},
    {countryCode: 'SG', dialCode: '+65', placeholder: '123456789'},
    {countryCode: 'SX', dialCode: '+1', placeholder: '123456789'},
    {countryCode: 'SK', dialCode: '+421', placeholder: '901234567'},
    {countryCode: 'SI', dialCode: '+386', placeholder: '40123456'},
    {countryCode: 'SB', dialCode: '+677', placeholder: '123456789'},
    {countryCode: 'SO', dialCode: '+252', placeholder: '123456789'},
    {countryCode: 'ZA', dialCode: '+27', placeholder: '123456789'},
    {countryCode: 'KR', dialCode: '+82', placeholder: '1012345678'},
    {countryCode: 'SS', dialCode: '+211', placeholder: '123456789'},
    {countryCode: 'ES', dialCode: '+34', placeholder: '612345678'},
    {countryCode: 'LK', dialCode: '+94', placeholder: '123456789'},
    {countryCode: 'SD', dialCode: '+249', placeholder: '123456789'},
    {countryCode: 'SR', dialCode: '+597', placeholder: '123456789'},
    {countryCode: 'SE', dialCode: '+46', placeholder: '701234567'},
    {countryCode: 'CH', dialCode: '+41', placeholder: '123456789'},
    {countryCode: 'SY', dialCode: '+963', placeholder: '123456789'},
    {countryCode: 'TW', dialCode: '+886', placeholder: '123456789'},
    {countryCode: 'TJ', dialCode: '+992', placeholder: '123456789'},
    {countryCode: 'TZ', dialCode: '+255', placeholder: '123456789'},
    {countryCode: 'TH', dialCode: '+66', placeholder: '123456789'},
    {countryCode: 'TL', dialCode: '+670', placeholder: '123456789'},
    {countryCode: 'TG', dialCode: '+228', placeholder: '123456789'},
    {countryCode: 'TK', dialCode: '+690', placeholder: '123456789'},
    {countryCode: 'TO', dialCode: '+676', placeholder: '123456789'},
    {countryCode: 'TT', dialCode: '+1', placeholder: '123456789'},
    {countryCode: 'TN', dialCode: '+216', placeholder: '123456789'},
    {countryCode: 'TR', dialCode: '+90', placeholder: '5321234567'},
    {countryCode: 'TM', dialCode: '+993', placeholder: '123456789'},
    {countryCode: 'TC', dialCode: '+1', placeholder: '123456789'},
    {countryCode: 'TV', dialCode: '+688', placeholder: '123456789'},
    {countryCode: 'UG', dialCode: '+256', placeholder: '123456789'},
    {countryCode: 'UA', dialCode: '+380', placeholder: '123456789'},
    {countryCode: 'AE', dialCode: '+971', placeholder: '123456789'},
    {countryCode: 'GB', dialCode: '+44', placeholder: '7911123456'},
    {countryCode: 'US', dialCode: '+1', placeholder: '4155552671'},
    {countryCode: 'VI', dialCode: '+1', placeholder: '123456789'},
    {countryCode: 'UY', dialCode: '+598', placeholder: '123456789'},
    {countryCode: 'UZ', dialCode: '+998', placeholder: '123456789'},
    {countryCode: 'VU', dialCode: '+678', placeholder: '123456789'},
    {countryCode: 'VA', dialCode: '+39', placeholder: '123456789'},
    {countryCode: 'VE', dialCode: '+58', placeholder: '123456789'},
    {countryCode: 'VN', dialCode: '+84', placeholder: '123456789'},
    {countryCode: 'WF', dialCode: '+681', placeholder: '123456789'},
    {countryCode: 'YE', dialCode: '+967', placeholder: '123456789'},
    {countryCode: 'ZM', dialCode: '+260', placeholder: '123456789'},
    {countryCode: 'ZW', dialCode: '+263', placeholder: '123456789'}
]

export const CONTINENT_MESSAGES = defineMessages({
    Africa: {id: 'global.continent.africa', defaultMessage: 'Africa'},
    Asia: {id: 'global.continent.asia', defaultMessage: 'Asia'},
    Europe: {id: 'global.continent.europe', defaultMessage: 'Europe'},
    North_America: {id: 'global.continent.northamerica', defaultMessage: 'North America'},
    Oceania: {id: 'global.continent.oceania', defaultMessage: 'Oceania'},
    South_America: {id: 'global.continent.southamerica', defaultMessage: 'South America'}
})

export const PROMOTION_BADGE_MESSAGES = defineMessages({
    'promo.shortheadline.extra': {
        id: 'promo.shortheadline.extra',
        defaultMessage: 'Extra %'
    },
    'promo.shortheadline.outlet_price': {
        id: 'promo.shortheadline.outlet_price',
        defaultMessage: 'Outlet'
    },
    'promo.shortheadline.outlet_price_10': {
        id: 'promo.shortheadline.outlet_price_10',
        defaultMessage: 'Outlet Price -10%'
    },
    'promo.shortheadline.outlet_price_30': {
        id: 'promo.shortheadline.outlet_price_30',
        defaultMessage: 'Outlet Price -30%'
    },
    'promo.shortheadline.outlet_price_50': {
        id: 'promo.shortheadline.outlet_price_50',
        defaultMessage: 'Outlet Price -50%'
    },
    'promo.shortheadline.last_chance': {
        id: 'promo.shortheadline.last_chance',
        defaultMessage: 'Last Chance'
    },
    'promo.shortheadline.last_chance_10': {
        id: 'promo.shortheadline.last_chance_10',
        defaultMessage: 'Last Chance -10%'
    },
    'promo.shortheadline.last_chance_30': {
        id: 'promo.shortheadline.last_chance_30',
        defaultMessage: 'Last Chance -30%'
    },
    'promo.shortheadline.last_chance_50': {
        id: 'promo.shortheadline.last_chance_50',
        defaultMessage: 'Last Chance -50%'
    },
    'promo.shortheadline.free_shipping': {
        id: 'promo.shortheadline.free_shipping',
        defaultMessage: 'Free Shipping'
    },
    'promo.shortheadline.shipping_on_us': {
        id: 'promo.shortheadline.shipping_on_us',
        defaultMessage: 'Shipping On Us'
    },
    'promo.shortheadline.deal': {
        id: 'promo.shortheadline.deal',
        defaultMessage: 'Black Friday'
    },
    'promo.shortheadline.special_price': {
        id: 'promo.shortheadline.special_price',
        defaultMessage: 'Special Price'
    },
    'promo.shortheadline.bonus': {
        id: 'promo.shortheadline.bonus',
        defaultMessage: 'Bonus'
    },
    'promo.shortheadline.bundle_offer': {
        id: 'promo.shortheadline.bundle_offer',
        defaultMessage: 'Bundle Offer'
    },
    'promo.shortheadline.buy_2_get_1': {
        id: 'promo.shortheadline.buy_2_get_1',
        defaultMessage: 'Buy 2 & Get 1'
    },
    'promo.shortheadline.buy_1_get_1': {
        id: 'promo.shortheadline.buy_1_get_1',
        defaultMessage: 'Buy 1 & Get 1'
    },
    'promo.shortheadline.special_offer': {
        id: 'promo.shortheadline.special_offer',
        defaultMessage: 'Special Offer'
    },
    'promo.shortheadline.final_offer': {
        id: 'promo.shortheadline.final_offer',
        defaultMessage: 'Final Offer'
    },
    'promo.shortheadline.limited_offer': {
        id: 'promo.shortheadline.limited_offer',
        defaultMessage: 'Limited Offer'
    },
    'promo.shortheadline.save_extra': {
        id: 'promo.shortheadline.save_extra',
        defaultMessage: 'Save Extra'
    }
})

export const GENERAL_FORM_VALIDATION_MESSAGES = defineMessages({
    minLength: {
        defaultMessage: `{label} can not be shorter than {n} characters.`,
        id: `use_address_fields.error.min_length_field`
    },
    maxLength: {
        defaultMessage: `{label} can not be longer than {n} characters.`,
        id: `use_address_fields.error.max_length_field`
    }
})

// aspect ratio for item image
export const ITEM_IMAGE_ASPECT = 7 / 9

// delay loading of tracking scripts to avoid blocking page load (in ms)
export const TRACKING_LOADING_DELAY = 8000

// Tracking promise will automatically resolve after this timeout (in ms)
export const TRACKING_TIMEOUT = 1000

// image type to display in pdp cockpit
export const MAIN_IMAGES_IMAGE_TYPE = 'mainview'

// image type to display in pdp xleap cockpit
export const XLEAP_MAIN_IMAGES_IMAGE_TYPE = 'main'

// maximum number of main set product images injected into the xleap cockpit gallery of a product set
export const MAIN_SET_PRODUCT_GALLERY_IMAGES_COUNT = 5

// number of product set images the injected main set product images follow, counted on the set
// combination filtered list without the brightcove videos placed among them
export const MAIN_SET_PRODUCT_IMAGES_PRECEDING_COUNT = 2

// image type to display in emotional gallery component
export const EMOTIONAL_GALLERY_IMAGE_TYPE = 'emotional1'

// number of star-icons available for a rating
export const RATING_MAX_STARS = 5

// value to determine if a halfStar is rendered
export const RATING_HALFSTAR_THRESHOLD = {ceilAt: 3, floorAt: 7}

// Responsive image width buckets for srcset generation.
// These are final physical pixel widths (DPR already factored in) — not CSS px.
// Derived by minimising byte waste across the device × width-config matrix;
// see scripts/image-analysis/find-optimal-buckets.js for the derivation.
export const RESPONSIVE_IMAGE_BUCKETS = [240, 768, 922, 1062, 1179, 1290, 1476, 1536, 1728, 2304]

// Default width for responsive images when no widths are specified
// 90vw provides reasonable default with some margin for UI chrome
export const DEFAULT_RESPONSIVE_IMAGE_WIDTH = '90vw'

// Threshold for responsive image size optimization
// If loaded image is smaller than this ratio of desired size, update to better quality
export const RESPONSIVE_IMAGE_SIZE_THRESHOLD = 0.75

//Number of words in product usp short description
export const PRODUCT_USP_SHORT_DESC_WORD_COUNT = 33

// image type to display in product tile component
export const PRODUCT_TILE_IMAGE_TYPE = 'mainview'

// Recommended accessories product type
export const RECOMMENDED_ACCESSORIES_TYPE = 1
export const RECOMMENDED_SPARE_PARTS_TYPE = 2
export const RECOMMENDED_ACCESSORY_CONFIGURATIONS_TYPE = 12

// keyboard key numbers
export const EnterKeyNumber = 13

// Month timestamp (1 Month: 2629743000 milliseconds (30.44 days on average))
export const MONTH_MS = 2629743000

// 6 months timestamp in milliseconds
export const NEW_PRODUCT_BADGE_DURATION = 6 * MONTH_MS

// Promotion badge key used for DEAL promotions
export const DEAL_PROMO_KEY = 'promo.shortheadline.deal'

// catalog constants
// STOKKE root category id
export const CATALOG_ROOT_CATEGORY_ID = '14799'

// list of regex that allow loading of external scripts inside cms modules
// NOTE 1: the script must also be allowed in BM csp configuration or helmet-config
// NOTE 2: the loading order is not guaranteed
export const CMS_ALLOWED_EXTERNAL_SCRIPT_SOURCES = [
    // brightcove video player
    /^https:\/\/players.brightcove.net\//i,
    // tiny-slider - temporary until cms modules are updated to use extTns
    /^https:\/\/cdnjs.cloudflare.com\/ajax\/libs\/tiny-slider\//i
]

export const VALIDATE_EMAIL_PATTERN = /^[\w.%+-]+@[\w.-]+\.[\w]{2,6}$/i

export const MINIMUN_NUM_OF_RECOMMENDED_PRODUCTS = 1
export const MAXIMUM_NUM_OF_RECOMMENDED_PRODUCTS = 12

export const MAXIMUM_NUM_OF_RECOMMENDED_ACCESSORIES = 10

// ID of the category CMS Content slot
export const CATEGORY_BANNER_SLOT_ID = 'cat-banner'
export const CATEGORY_BANNER_PROMO_TOP = 'plp-promo-top'
export const CATEGORY_BANNER_PROMO_MIDDLE = 'plp-promo-middle'

// content for homepage
export const HOME_CONTENT_ASSET_ID = 'root_velocity'

// list of refinements that are not displayed in the selected refinements list
export const HIDDEN_REFINEMENTS = ['cgid', 'htype', 'pmid', 'available-on-web']

export const CRO_PUBLIC_API_NAME = '__cro'

// list of CRO locations
export const CRO_LOCATION = {
    ADD_TO_CART_MODAL: 'addToCartModal',
    CHECKOUT_PAGE: 'checkout',
    CHECKOUT_ORDER_SUMMARY: 'checkoutOrderSummary',
    CHECKOUT_ORDER_SUMMARY_AFTER: 'checkoutOrderSummaryAfter',
    APPCART: 'appcart',
    NAVIGATION_OVERLAY: 'navigationOverlay'
}

export const TILE_CONTEXT_ACCESSORIES = 'matching_acc'
export const TILE_CONTEXT_FANREEL_RELATED_PRODUCTS = 'fanreel_related_products'

export const CRO_EXPERIMENTS = {
    cro63PdpHideRating: {experimentId: 'cro63', variantId: 'variant1'},
    cro65PdpHideDeliveryTime: {experimentId: 'cro65', variantId: 'variant1'},
    cro69CheckoutOrderSummary: {experimentId: 'cro69', variantId: 'variant1'},
    cro71ProductImages: {experimentId: 'cro71', variantId: 'variant1'},
    cro73NewsletterSubscription: {
        experimentId: 'cro73',
        variant1: 'variant1',
        variant2: 'variant2',
        variant3: 'variant3'
    }
}

/**
 * If the page was hidden for at least this interval in milliseconds then
 * on next page show a virtual page load event will be emitted to the data layer
 * @type {number}
 */
export const INTERVAL_TO_EMIT_VIRTUAL_PAGE_EVENT = 30 * 60 * 1000 // 30 minutes

/**
 * Prefix for local storage keys used by BV feedback
 * @type {string}
 */
export const BV_LOCAL_STORAGE_KEY_PREFIX = 'bv-'

/**
 * Default limit for the curalate feed (api default is 10)
 * @type {number}
 * @see https://developers.bazaarvoice.com/v1.0-SocialCommerceAPI/docs/display-media
 */
export const DEFAULT_CURALATE_FEED_LIMIT = 12

/**
 * Default limit for the reviews (api default is 10)
 * @type {number}
 * @see https://developer.bazaarvoice.com/conversations-api/tutorials/display/pagination
 */
export const BV_REVIEWS_LIMIT = 5

/**
 * Navigation tracking level Hero (Header navigation links)
 * @type {string}
 */
export const NAVIGATION_TRACKING_LEVEL_HERO_LABEL = 'menu_hero'

/**
 * Navigation tracking level 1
 * @type {string}
 */
export const NAVIGATION_TRACKING_LEVEL_1_LABEL = 'menu_level_1'

/**
 * Navigation tracking level 2
 * @type {string}
 */
export const NAVIGATION_TRACKING_LEVEL_2_LABEL = 'menu_level_2'

/**
 * Navigation tracking level 3
 * @type {string}
 */
export const NAVIGATION_TRACKING_LEVEL_3_LABEL = 'menu_level_3'

/**
 * Navigation level groups
 * @type {string}
 */
export const NAVIGATION_TRACKING_LEVEL_GROUPS_LABEL = 'menu_level_groups'

/**
 * CSP Header Name
 * @type {string}
 */
export const CONTENT_SECURITY_POLICY = 'content-security-policy'

/**
 * Prefix for local storage keys used by CPSDP configuration
 * @type {string}
 */
export const CPSDP_CONFIGURATION_STORAGE_KEY_PREFIX = 'cpsdp_'

/**
 * Maximum number of ids that can be requested in a single getProducts request
 * @type {number}
 */
export const MAX_IDS_PER_GET_PRODUCTS_REQUEST = 23

export const CONFIRMATION_DIALOG_DEFAULT_CONFIG = defineMessages({
    dialogTitle: {
        defaultMessage: 'Confirm Action',
        id: 'confirmation_modal.default.title.confirm_action'
    },
    confirmationMessage: {
        defaultMessage: 'Are you sure you want to continue?',
        id: 'confirmation_modal.default.message.you_want_to_continue'
    },
    primaryActionLabel: {defaultMessage: 'Yes', id: 'confirmation_modal.default.action.yes'},
    alternateActionLabel: {defaultMessage: 'No', id: 'confirmation_modal.default.action.no'}
})

/**
 * Key for dashboard page in my stokke config
 */
export const MY_STOKKE_DASHBOARD_PAGE_ID = 'dashboard'
/**
 * Key for personal information page in my stokke config
 */
export const MY_STOKKE_PERSONAL_INFORMATION_PAGE_ID = 'personal-information'
/**
 * Key for warranty page in my stokke config
 */
export const MY_STOKKE_WARRANTY_PAGE_ID = 'warranty'
/**
 * Key for orders page in my stokke config
 */
export const MY_STOKKE_ORDERS_PAGE_ID = 'orders'
/**
 * Key for addresses page in my stokke config
 */
export const MY_STOKKE_ADDRESSES_PAGE_ID = 'addresses'
/**
 * Key for wishlist page in my stokke config
 */
export const MY_STOKKE_WISHLIST_PAGE_ID = 'wishlist'

/**
 * GTM Register error messages
 */
export const GTM_REGISTER_ERROR_MESSAGES = {
    firstName: 'Invalid first name',
    lastName: 'Invalid last name',
    email: 'Invalid email address',
    password: 'Invalid password'
}

/**
 * Storefront pretection user name
 */
export const STOREFRONT_USERNAME = 'storefront'

/**
 * Local Storage key for enabling logging
 */
export const STOKKE_DEVELOPMENT_LOG = 'stokke-development-log'

/**
 * Timeout in ms for debouncing the cart item quantity change
 * @type {number}
 */
export const CART_ITEM_QUANTITY_DEBOUNCE_WAIT = 750

// Contant to enable PWA kit reset password flow
// @see https://github.com/SalesforceCommerceCloud/pwa-kit/pull/2079/files#diff-010df54b1ffccac03a90c74cd8c2556b01ff9e7563a509ef0f046e735c72dae4
export const PWA_RESET_PASSWORD_ENABLED = false
// Constant to enable PWA kit do-not-track notification
// @see https://github.com/SalesforceCommerceCloud/pwa-kit/pull/2203
export const PWA_DNT_NOTIFICATION_ENABLED = false

/**
 * Confirmation dialog parameters to confirm removal of item from cart
 */
export const REMOVE_CART_ITEM_CONFIRMATION_DIALOG_CONFIG = {
    dialogTitle: defineMessage({
        defaultMessage: 'Confirm Remove Item',
        id: 'confirmation_modal.remove_cart_item.title.confirm_remove'
    }),
    confirmationMessage: defineMessage({
        defaultMessage: 'Are you sure you want to remove this item?',
        id: 'confirmation_modal.remove_cart_item.message.sure_to_remove'
    }),
    primaryActionLabel: defineMessage({
        defaultMessage: 'Yes, remove item',
        id: 'confirmation_modal.remove_cart_item.action.yes'
    }),
    alternateActionLabel: defineMessage({
        defaultMessage: 'No, keep item',
        id: 'confirmation_modal.remove_cart_item.action.no'
    })
}

export const SUPPORTED_STORE_LOCATOR_COUNTRIES = [
    {
        countryCode: 'US',
        countryName: defineMessage({
            defaultMessage: 'United States',
            id: 'store_locator.dropdown.united_states'
        })
    },
    {
        countryCode: 'DE',
        countryName: defineMessage({
            defaultMessage: 'Germany',
            id: 'store_locator.dropdown.germany'
        })
    }
]

export const DEFAULT_STORE_LOCATOR_COUNTRY = {
    countryCode: 'DE',
    countryName: defineMessage({
        defaultMessage: 'Germany',
        id: 'store_locator.dropdown.germany'
    })
}
export const DEFAULT_STORE_LOCATOR_POSTAL_CODE = '10178'
export const STORE_LOCATOR_DISTANCE = 100
export const STORE_LOCATOR_NUM_STORES_PER_LOAD = 10
export const STORE_LOCATOR_DISTANCE_UNIT = 'km'
// shorter typed text geocodes to unrelated places
export const STORE_LOCATOR_MIN_ADDRESS_LENGTH = 3
// Constant to enable PWA kit store locator
// @see https://github.com/SalesforceCommerceCloud/pwa-kit/pull/1922
// @see https://github.com/SalesforceCommerceCloud/pwa-kit/pulls?q=%22store+locator%22+in%3Atitle+is%3Apr+is%3Amerged
export const STORE_LOCATOR_IS_ENABLED = true

export const DEFAULT_DNT_STATE = false
// Constants for shopper context
// Supported non-string field types used in SHOPPER_CONTEXT_SEARCH_PARAMS below.
// Only non-string types need to be identified using the "type" field.
// If no "type" field is present, the value will be parsed as a string by default.
export const SHOPPER_CONTEXT_FIELD_TYPES = {
    INT: 'int',
    DOUBLE: 'double',
    ARRAY: 'array'
}
export const SHOPPER_CONTEXT_SEARCH_PARAMS = {
    sourceCode: {paramName: 'sourceCode'},
    geoLocation: {
        city: {paramName: 'city'},
        country: {paramName: 'country'},
        countryCode: {paramName: 'countryCode'},
        latitude: {paramName: 'latitude', type: SHOPPER_CONTEXT_FIELD_TYPES.DOUBLE},
        longitude: {paramName: 'longitude', type: SHOPPER_CONTEXT_FIELD_TYPES.DOUBLE},
        metroCode: {paramName: 'metroCode'},
        postalCode: {paramName: 'postalCode'},
        region: {paramName: 'region'},
        regionCode: {paramName: 'regionCode'}
    },
    customQualifiers: {
        // Add custom qualifiers here
    },
    assignmentQualifiers: {
        // Add assignment qualifiers here
    }
}

// Constants for Login
export const LOGIN_TYPES = {
    PASSWORD: 'password',
    PASSWORDLESS: 'passwordless',
    SOCIAL: 'social'
}

export const CUSTOMER_ACTIVATION_TOKEN_QUERY_KEY = 'token'

// Constants for Password Reset
export const RESET_PASSWORD_LANDING_PATH = '/reset-password-landing'

// Constants for Passwordless Login
export const PASSWORDLESS_LOGIN_LANDING_PATH = '/passwordless-login-landing'

export const PASSWORDLESS_ERROR_MESSAGES = [
    /callback_uri doesn't match/i,
    /passwordless permissions error/i,
    /client secret is not provided/i
]

export const PASSWORDLESS_LOGIN_TOKEN_QUERY_KEY = 'pwlstoken'

export const INVALID_TOKEN_ERROR = /invalid token/i

export const USER_NOT_FOUND_ERROR = /user not found/i

/**
 * Maximum quantity of product that can be ordered simultaneously by D2B customers
 * when `d2bMaxQuantity` site pref is not positive number
 * @type {number}
 */
export const MAX_D2B_ORDER_QUANTITY = 999
/**
 * Maximum quantity of product that can be ordered simultaneously by B2C customers
 * when `productPurchaseLimit` site pref is not positive number
 * @type {number}
 */
export const MAX_B2C_ORDER_QUANTITY = 5

/**
 * Product list to hide wishlist icon
 * @type {Array.<string>}
 */
export const HIDE_WISHLIST_TOGGLE = ['583801', '583701', '9800*']

//Constants for accessibility
export const HEADER_MENU_CONTENT_ID = 'header-menu-content'

/**
 * Maximum quantity of products that can receive bonus products
 * @type {number}
 */
export const MAX_BONUS_PRODUCT_QUANTITY = 3

export const CUSTOM_RECOMMENDATION_BESTSELLER_RULE_ID = 'bestSeller'

//This is an API limit and is therefore not configurable
export const NUM_STORES_PER_REQUEST_API_MAX = 200

export const CONTEXT_NOT_PROVIDED = Symbol('CONTEXT_NOT_PROVIDED')

// Use these constants to open cart with specific error message
export const CART_ERROR_FAIL_ORDER = 'fail_order'
export const CART_ERROR_API_ERROR = 'api_error'
export const CART_ERRORS = {
    [CART_ERROR_FAIL_ORDER]: FAIL_ORDER_ERROR_MESSAGE,
    [CART_ERROR_API_ERROR]: API_ERROR_MESSAGE
}

// max characters allowed for product engraving
export const MAX_ENGRAVING_CHARACTERS = 14
export const ENGRAVING_TEXT_PATTERN =
    /^(?! )[A-Za-z\xAA\xB5\xBA\xC0-\xD6\xD8-\xF6\xF8-\u02C1\u02C6-\u02D1\u02E0-\u02E4\u02EC\u02EE\u0370-\u0374\u0376\u0377\u037A-\u037D\u037F\u0386\u0388-\u038A\u038C\u038E-\u03A1\u03A3-\u03F5\u03F7-\u0481\u048A-\u052F\u0531-\u0556\u0559\u0560-\u0588\u05D0-\u05EA\u05EF-\u05F2\u0620-\u064A\u066E\u066F\u0671-\u06D3\u06D5\u06E5\u06E6\u06EE\u06EF\u06FA-\u06FC\u06FF\u0710\u0712-\u072F\u074D-\u07A5\u07B1\u07CA-\u07EA\u07F4\u07F5\u07FA\u0800-\u0815\u081A\u0824\u0828\u0840-\u0858\u0860-\u086A\u08A0-\u08B4\u08B6-\u08BD\u0904-\u0939\u093D\u0950\u0958-\u0961\u0971-\u0980\u0985-\u098C\u098F\u0990\u0993-\u09A8\u09AA-\u09B0\u09B2\u09B6-\u09B9\u09BD\u09CE\u09DC\u09DD\u09DF-\u09E1\u09F0\u09F1\u09FC\u0A05-\u0A0A\u0A0F\u0A10\u0A13-\u0A28\u0A2A-\u0A30\u0A32\u0A33\u0A35\u0A36\u0A38\u0A39\u0A59-\u0A5C\u0A5E\u0A72-\u0A74\u0A85-\u0A8D\u0A8F-\u0A91\u0A93-\u0AA8\u0AAA-\u0AB0\u0AB2\u0AB3\u0AB5-\u0AB9\u0ABD\u0AD0\u0AE0\u0AE1\u0AF9\u0B05-\u0B0C\u0B0F\u0B10\u0B13-\u0B28\u0B2A-\u0B30\u0B32\u0B33\u0B35-\u0B39\u0B3D\u0B5C\u0B5D\u0B5F-\u0B61\u0B71\u0B83\u0B85-\u0B8A\u0B8E-\u0B90\u0B92-\u0B95\u0B99\u0B9A\u0B9C\u0B9E\u0B9F\u0BA3\u0BA4\u0BA8-\u0BAA\u0BAE-\u0BB9\u0BD0\u0C05-\u0C0C\u0C0E-\u0C10\u0C12-\u0C28\u0C2A-\u0C39\u0C3D\u0C58-\u0C5A\u0C60\u0C61\u0C80\u0C85-\u0C8C\u0C8E-\u0C90\u0C92-\u0CA8\u0CAA-\u0CB3\u0CB5-\u0CB9\u0CBD\u0CDE\u0CE0\u0CE1\u0CF1\u0CF2\u0D05-\u0D0C\u0D0E-\u0D10\u0D12-\u0D3A\u0D3D\u0D4E\u0D54-\u0D56\u0D5F-\u0D61\u0D7A-\u0D7F\u0D85-\u0D96\u0D9A-\u0DB1\u0DB3-\u0DBB\u0DBD\u0DC0-\u0DC6\u0E01-\u0E30\u0E32\u0E33\u0E40-\u0E46\u0E81\u0E82\u0E84\u0E86-\u0E8A\u0E8C-\u0EA3\u0EA5\u0EA7-\u0EB0\u0EB2\u0EB3\u0EBD\u0EC0-\u0EC4\u0EC6\u0EDC-\u0EDF\u0F00\u0F40-\u0F47\u0F49-\u0F6C\u0F88-\u0F8C\u1000-\u102A\u103F\u1050-\u1055\u105A-\u105D\u1061\u1065\u1066\u106E-\u1070\u1075-\u1081\u108E\u10A0-\u10C5\u10C7\u10CD\u10D0-\u10FA\u10FC-\u1248\u124A-\u124D\u1250-\u1256\u1258\u125A-\u125D\u1260-\u1288\u128A-\u128D\u1290-\u12B0\u12B2-\u12B5\u12B8-\u12BE\u12C0\u12C2-\u12C5\u12C8-\u12D6\u12D8-\u1310\u1312-\u1315\u1318-\u135A\u1380-\u138F\u13A0-\u13F5\u13F8-\u13FD\u1401-\u166C\u166F-\u167F\u1681-\u169A\u16A0-\u16EA\u16F1-\u16F8\u1700-\u170C\u170E-\u1711\u1720-\u1731\u1740-\u1751\u1760-\u176C\u176E-\u1770\u1780-\u17B3\u17D7\u17DC\u1820-\u1878\u1880-\u1884\u1887-\u18A8\u18AA\u18B0-\u18F5\u1900-\u191E\u1950-\u196D\u1970-\u1974\u1980-\u19AB\u19B0-\u19C9\u1A00-\u1A16\u1A20-\u1A54\u1AA7\u1B05-\u1B33\u1B45-\u1B4B\u1B83-\u1BA0\u1BAE\u1BAF\u1BBA-\u1BE5\u1C00-\u1C23\u1C4D-\u1C4F\u1C5A-\u1C7D\u1C80-\u1C88\u1C90-\u1CBA\u1CBD-\u1CBF\u1CE9-\u1CEC\u1CEE-\u1CF3\u1CF5\u1CF6\u1CFA\u1D00-\u1DBF\u1E00-\u1F15\u1F18-\u1F1D\u1F20-\u1F45\u1F48-\u1F4D\u1F50-\u1F57\u1F59\u1F5B\u1F5D\u1F5F-\u1F7D\u1F80-\u1FB4\u1FB6-\u1FBC\u1FBE\u1FC2-\u1FC4\u1FC6-\u1FCC\u1FD0-\u1FD3\u1FD6-\u1FDB\u1FE0-\u1FEC\u1FF2-\u1FF4\u1FF6-\u1FFC\u2071\u207F\u2090-\u209C\u2102\u2107\u210A-\u2113\u2115\u2119-\u211D\u2124\u2126\u2128\u212A-\u212D\u212F-\u2139\u213C-\u213F\u2145-\u2149\u214E\u2183\u2184\u2C00-\u2C2E\u2C30-\u2C5E\u2C60-\u2CE4\u2CEB-\u2CEE\u2CF2\u2CF3\u2D00-\u2D25\u2D27\u2D2D\u2D30-\u2D67\u2D6F\u2D80-\u2D96\u2DA0-\u2DA6\u2DA8-\u2DAE\u2DB0-\u2DB6\u2DB8-\u2DBE\u2DC0-\u2DC6\u2DC8-\u2DCE\u2DD0-\u2DD6\u2DD8-\u2DDE\u2E2F\u3005\u3006\u3031-\u3035\u303B\u303C\u3041-\u3096\u309D-\u309F\u30A1-\u30FA\u30FC-\u30FF\u3105-\u312F\u3131-\u318E\u31A0-\u31BA\u31F0-\u31FF\u3400-\u4DB5\u4E00-\u9FEF\uA000-\uA48C\uA4D0-\uA4FD\uA500-\uA60C\uA610-\uA61F\uA62A\uA62B\uA640-\uA66E\uA67F-\uA69D\uA6A0-\uA6E5\uA717-\uA71F\uA722-\uA788\uA78B-\uA7BF\uA7C2-\uA7C6\uA7F7-\uA801\uA803-\uA805\uA807-\uA80A\uA80C-\uA822\uA840-\uA873\uA882-\uA8B3\uA8F2-\uA8F7\uA8FB\uA8FD\uA8FE\uA90A-\uA925\uA930-\uA946\uA960-\uA97C\uA984-\uA9B2\uA9CF\uA9E0-\uA9E4\uA9E6-\uA9EF\uA9FA-\uA9FE\uAA00-\uAA28\uAA40-\uAA42\uAA44-\uAA4B\uAA60-\uAA76\uAA7A\uAA7E-\uAAAF\uAAB1\uAAB5\uAAB6\uAAB9-\uAABD\uAAC0\uAAC2\uAADB-\uAADD\uAAE0-\uAAEA\uAAF2-\uAAF4\uAB01-\uAB06\uAB09-\uAB0E\uAB11-\uAB16\uAB20-\uAB26\uAB28-\uAB2E\uAB30-\uAB5A\uAB5C-\uAB67\uAB70-\uABE2\uAC00-\uD7A3\uD7B0-\uD7C6\uD7CB-\uD7FB\uF900-\uFA6D\uFA70-\uFAD9\uFB00-\uFB06\uFB13-\uFB17\uFB1D\uFB1F-\uFB28\uFB2A-\uFB36\uFB38-\uFB3C\uFB3E\uFB40\uFB41\uFB43\uFB44\uFB46-\uFBB1\uFBD3-\uFD3D\uFD50-\uFD8F\uFD92-\uFDC7\uFDF0-\uFDFB\uFE70-\uFE74\uFE76-\uFEFC\uFF21-\uFF3A\uFF41-\uFF5A\uFF66-\uFFBE\uFFC2-\uFFC7\uFFCA-\uFFCF\uFFD2-\uFFD7\uFFDA-\uFFDC\u30000-9-_ ]+(?<! )$/

// Estimated delivery helper text
export const ESTIMATED_DELIVERY_HELPER_TEXT = defineMessage({
    id: 'shipping_method.tooltip.delivery_dates_are_estimated',
    defaultMessage:
        'Delivery dates are estimated. Enter your postal code at checkout for a more accurate date.'
})
